import { useQueries, useQuery } from '@tanstack/react-query';
import type { Competition } from '@uefadigital/panenka';
import { TeamType } from '@uefadigital/panenka';
import { useState, useEffect, memo, useMemo } from 'react';
import { Route, Routes, BrowserRouter as Router } from 'react-router';

import { convertToSlug } from '@cobalt/utilities';
import { useRoutedContent } from '@hooks/useRoutedContent';
import { getTeamCompetitionsQuery } from '@queries/competition';
import { getAssociationTeamsQuery } from '@queries/teams';
import { hydrateWithTranslations } from '@services/TranslatorService';
import PanenkaDropdown from '@shared/PanenkaDropdown';
import { useTranslateFn } from '@shared/Translate';
import type { Team } from '@shared-types/match.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';

import * as styles from './styles.module.scss';

const excludedCompetitions = [{ id: '40' }];

const TeamButtonContent = ({ element: team }: { element: Team }) => {
  const t = useTranslateFn();
  return (
    <div className={styles.triggerWrapper}>
      <span className={styles.label}>{t('selected-team')}</span>
      <span className={styles.name}>{team && t(team.teamTypeDetail)}</span>
    </div>
  );
};
const TeamMenuItemContent = ({ element: team }: { element: Team }) => {
  const t = useTranslateFn();
  return <span className={styles.menuItem}>{t(team.teamTypeDetail)}</span>;
};

const CompButtonContent = ({ element: c }: { element: Competition }) => {
  const t = useTranslateFn();
  return (
    <div className={styles.triggerWrapper}>
      <span className={styles.label}>{t('selected-competition')}</span>
      <span className={styles.name}>{hydrateWithTranslations(c)?.name}</span>
    </div>
  );
};
const CompMenuItemContent = ({ element: c }: { element: Competition }) => (
  <span className={styles.menuItem}>{hydrateWithTranslations(c).name}</span>
);

const Dropdown = ({ options }) => {
  const [selectedTeam, setSelectedTeam] = useState<Team>(null);

  const associationId = options.association.associationId;

  const { data: teams } = useQuery<Team[]>({
    ...getAssociationTeamsQuery({
      associationId,
      limit: 50,
      offset: 0,
      phase: 'ALL',
      teamType: TeamType.NATIONAL,
    }),
    select: (data) =>
      options.competitionsFilter.map((type) => data.find((t) => t.teamTypeDetail === type)).filter(Boolean),
  });

  const { competitions } = useQueries({
    queries: teams.map((team) => ({
      ...getTeamCompetitionsQuery({ teamId: team.id }),
      select: (comps: Competition[]) => [
        team.id,
        comps?.filter((c) => !excludedCompetitions.some((e) => e.id === c.id)),
      ],
    })),
    combine: (result) => ({
      competitions: Object.fromEntries(result.map((d) => d.data)) as Record<string, Competition[]>,
    }),
  });

  useEffect(() => {
    const defaultTeam = teams?.find((element) => element.teamTypeDetail === 'NATIONAL_MEN_TEAM_A');

    if (!defaultTeam) return;

    setSelectedTeam(defaultTeam);
  }, [teams]);

  const teamCompetitions = useMemo(
    () => selectedTeam?.id && competitions?.[selectedTeam.id],
    [competitions, selectedTeam?.id],
  );

  const onContentLoaded = (content: string) => {
    const target = document.querySelector<HTMLElement>(options.targetSelector);
    target.innerHTML = content;
  };

  const { selectedItem: selectedCompetition, updateSelectedItem: updateSelectedCompetition } =
    useRoutedContent<Competition>(
      teamCompetitions,
      (c) => `/${convertToSlug(c.id, hydrateWithTranslations(c).name)}/`,
      (c) => options.libraryPath.replace('{id}', c.id),
      onContentLoaded,
      options.useRouting,
    );

  const handleSelectedTeam = (team: Team) => {
    GoogleTagManagerHelper.event(
      'Click',
      'Select team',
      'ma national teams',
      { interaction: 'ma national teams' },
      'select_team',
    );
    setSelectedTeam(team);
  };

  const handleSelectedCompetition = (competition: Competition) => {
    GoogleTagManagerHelper.event(
      'Click',
      'Select competition',
      'ma national teams',
      { interaction: 'ma national teams' },
      'select_competition',
    );
    updateSelectedCompetition(competition);
  };

  const playingTeams = useMemo(() => teams?.filter((team) => !!competitions?.[team.id]?.length), [teams, competitions]);

  return (
    <div className={styles.wrapper}>
      <PanenkaDropdown<Team>
        multipleDropdowns
        elements={playingTeams}
        selectedItem={selectedTeam}
        onSelect={handleSelectedTeam}
        iconColor='text-01'
        ButtonContent={TeamButtonContent}
        MenuItemContent={TeamMenuItemContent}
      />
      {!!teamCompetitions?.length && (
        <PanenkaDropdown<Competition>
          multipleDropdowns
          elements={teamCompetitions}
          selectedItem={selectedCompetition}
          onSelect={handleSelectedCompetition}
          iconColor='text-01'
          ButtonContent={CompButtonContent}
          MenuItemContent={CompMenuItemContent}
        />
      )}
    </div>
  );
};

const RoutedDropdown = ({ options }) => (
  <Router basename={options.useRouting ? options.deepLinkBaseUrl : location.pathname}>
    <Routes>
      <Route path='/' element={<Dropdown options={options} />} />
      {options.useRouting && <Route path='/:id' element={<Dropdown options={options} />} />}
    </Routes>
  </Router>
);

export default memo(RoutedDropdown);
